/**
 * Client-side design tokens for the PDF-faithful PQR Preview canvas.
 *
 * MUST stay in sync with `server/src/pdf/pqrTheme.ts`. We deliberately do
 * NOT import from the server package — keeping a small mirror here avoids
 * a build-time cross-package dependency. Any palette/typography change
 * must be made in both places.
 */

export const palette = {
  /** Section banner background (CRC red). Matches PDF `palette.orange`. */
  banner: '#EB1C37',
  /** Deep red for table dividers within a section. Matches `palette.orangeDeep`. */
  bannerDeep: '#CC0000',
  /** Subtle red wash sometimes used as a hover/highlight background. */
  bannerSubtle: '#fff4e6',
  text: '#2d3748',
  textMuted: '#666666',
  textSubtle: '#718096',
  border: '#d1d5db',
  borderHairline: '#e2e8f0',
  surface: '#ffffff',
  surfaceMuted: '#f8f9fa',
  /** Dirty indicator (Tailwind amber-500). */
  dirty: '#f59e0b',
  /** Dirty section header background tint. */
  dirtySurface: '#fffbeb'
} as const

/**
 * Pt-equivalent sizes scaled for the on-screen replica. The PDF uses pt
 * at 72dpi; the HTML preview targets 96dpi screens, so 1pt ≈ 1.333px.
 * We pre-multiply here so callers can drop the values straight into CSS.
 */
export const typography = {
  family: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  title: '14.6px', // 11pt
  sectionHeader: '10.7px', // 8pt
  body: '9.3px', // 7pt
  caption: '8.7px', // 6.5pt
  micro: '8px' // 6pt
} as const

export const spacing = {
  page: '16px',
  section: '4px',
  row: '2px',
  cell: '2px'
} as const

/**
 * A3 PORTRAIT, scaled to fit container width via aspect-ratio.
 * Width-to-height ratio is 297mm / 420mm; max-width tuned so the on-screen
 * preview height fits a typical 1080p viewport without scrolling all
 * sections at once.
 */
export const canvasFrame = {
  aspectRatio: '297 / 420',
  maxWidth: '1000px'
} as const

/**
 * Floor Sheet preview — same width as portrait PQR; height grows with content.
 */
export const floorSheetCanvasFrame = {
  maxWidth: canvasFrame.maxWidth,
  width: canvasFrame.maxWidth,
  minHeight: '707px'
} as const
